<template>
  <div
    class="entity-list-page learning-catalog-page"
    :class="{ 'consult-active': activeView === 'agent' && heroSubmitted }"
  >
    <!-- Catalog — left-anchored spoke header -->
    <header v-if="activeView === 'browse'" class="list-header">
      <div>
        <h1>{{ hubTitle }}</h1>
        <p class="list-sub">{{ hubSubtitle }}</p>
      </div>
      <div class="header-actions">
        <router-link to="/learning/my-courses" class="btn btn-secondary">
          <i class="fas fa-user-graduate"></i>
          My Courses
        </router-link>
      </div>
    </header>

    <!-- Vet Consult entry — left-anchored (same chrome as other spokes) -->
    <template v-if="activeView === 'agent' && !heroSubmitted">
      <header class="list-header">
        <div>
          <h1>Vet Consult</h1>
          <p class="list-sub">
            Evidence-based answers grounded in peer-reviewed veterinary literature,
            with relevant CE courses surfaced alongside every response.
          </p>
        </div>
      </header>

      <div class="consult-entry">
        <div class="hero-depth-toggle" role="group" aria-label="Consult depth">
          <button
            type="button"
            :class="['hero-depth-btn', { active: heroDepth === 'quick' }]"
            @click="heroDepth = 'quick'"
            title="Quick: fast answer with key sources"
          >
            <i class="fas fa-bolt"></i> Quick
          </button>
          <button
            type="button"
            :class="['hero-depth-btn', { active: heroDepth === 'deep' }]"
            @click="heroDepth = 'deep'"
            title="Deep: comprehensive literature review"
          >
            <i class="fas fa-microscope"></i> Deep
          </button>
        </div>

        <div class="hero-search-wrap">
          <div class="hero-search-box" :class="{ focused: heroFocused }">
            <i class="fas fa-search hero-search-icon"></i>
            <input
              ref="heroInput"
              v-model="heroQuery"
              type="text"
              class="hero-search-input"
              placeholder="e.g. What is the meloxicam dose for a 10 kg dog with osteoarthritis?"
              @focus="heroFocused = true"
              @blur="heroFocused = false"
              @keydown.enter.prevent="submitHeroSearch"
            />
            <button
              class="hero-search-btn"
              :disabled="!heroQuery.trim()"
              @click="submitHeroSearch"
            >
              <i class="fas fa-arrow-right"></i>
            </button>
          </div>
        </div>

        <div class="hero-suggestions">
          <p class="suggestions-label">Try one of these:</p>
          <div class="suggestions-grid">
            <button
              v-for="prompt in suggestedPrompts"
              :key="prompt"
              class="suggestion-chip"
              @click="useHeroPrompt(prompt)"
            >
              <i class="fas fa-comment-medical"></i>
              {{ prompt }}
            </button>
          </div>
        </div>

        <div class="secondary-nav">
          <router-link to="/learning/my-courses" class="secondary-nav-link">
            <i class="fas fa-user-graduate"></i> My Courses
          </router-link>
          <router-link to="/learning/search" class="secondary-nav-link">
            <i class="fas fa-search"></i> Search Library
          </router-link>
          <button class="secondary-nav-link" @click="setActiveView('browse')">
            <i class="fas fa-th-large"></i> Browse All Courses
          </button>
        </div>
      </div>
    </template>

    <!-- Active consult chat -->
    <LmsAgentChat
      v-if="activeView === 'agent' && heroSubmitted"
      :key="chatKey"
      :initial-query="pendingQuery"
      :initial-session-id="pendingSessionId"
      :initial-depth="heroDepth"
      :compliance-subject-id="complianceSubjectId"
      @view-course="handleViewCourse"
      @switch-tab="setActiveView"
    />

    <!-- Catalog grid -->
    <div v-else-if="activeView === 'browse'" class="view-content">
      <CourseCatalog />
    </div>
  </div>
</template>

<script>
import { ref, computed, nextTick, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import CourseCatalog from '@/components/lms/CourseCatalog.vue'
import LmsAgentChat from '@/components/lms/LmsAgentChat.vue'
import auth from '@/services/auth'
import whitelabelBranding from '@/services/whitelabelBrandingService'
import {
  isLearningPortalOrg,
  isVmaeDemoOrgSlug,
  resolveOrgSlugFromHost,
} from '@/utils/associationPortal'

export default {
  name: 'LearningCatalog',
  components: {
    CourseCatalog,
    LmsAgentChat,
  },
  setup() {
    const router = useRouter()
    const route = useRoute()

    const validTabs = ['agent', 'browse']

    const learningOrgSlug = () => (
      auth.getUser()?.organization?.slug
      || route.query.org
      || resolveOrgSlugFromHost()
    )

    const resolveTab = (tab) => {
      if (tab === 'search') return 'agent'
      if (validTabs.includes(tab)) return tab
      // Vet Media / association tenants land on Vet Consult, not the catalog.
      return isLearningPortalOrg(learningOrgSlug()) ? 'agent' : 'browse'
    }

    const activeView = ref(resolveTab(route.query.tab))

    const complianceSubjectId = ref(
      route.query.compliance_subject_id ? String(route.query.compliance_subject_id) : '',
    )

    // Hero search state
    const heroInput = ref(null)
    const heroQuery = ref('')
    const heroFocused = ref(false)
    const heroSubmitted = ref(false)
    const heroDepth = ref('quick')
    const pendingQuery = ref('')
    const pendingSessionId = ref('')
    const chatKey = ref(0)

    // If arriving via Re-run from My Library, skip the hero
    const autorunQuery = route.query.autorun ? String(route.query.autorun) : ''
    if (autorunQuery) {
      pendingQuery.value = autorunQuery
      heroSubmitted.value = true
    }

    // Return from video player — reopen the consult session
    const sessionParam = route.query.session ? String(route.query.session) : ''
    if (sessionParam) {
      pendingSessionId.value = sessionParam
      heroSubmitted.value = true
    }

    const buildRouteQuery = (tab = activeView.value) => {
      const nextQuery = { tab }
      if (complianceSubjectId.value) {
        nextQuery.compliance_subject_id = complianceSubjectId.value
      }
      return nextQuery
    }

    const syncRouteQuery = () => {
      router.replace({ query: buildRouteQuery() })
    }

    if (autorunQuery || sessionParam) {
      syncRouteQuery()
    } else if (!route.query.tab) {
      syncRouteQuery()
    }

    watch(activeView, (tab) => {
      const current = route.query
      if (
        current.tab === tab
        && String(current.compliance_subject_id || '') === complianceSubjectId.value
        && !current.autorun
        && !current.session
      ) {
        return
      }
      router.replace({ query: buildRouteQuery(tab) })
    })

    watch(
      () => route.query.tab,
      (tab) => {
        if (tab === 'browse' || tab === 'agent') {
          activeView.value = tab
        } else if (!tab && (route.path === '/learning' || route.path === '/learning/')) {
          activeView.value = resolveTab(null)
        }
      },
    )

    watch(
      () => route.query.compliance_subject_id,
      (id) => {
        complianceSubjectId.value = id ? String(id) : ''
      },
    )

    function setActiveView(tab) {
      if (validTabs.includes(tab)) {
        activeView.value = tab
      }
    }

    const suggestedPrompts = [
      'Meloxicam dose for a 10 kg dog with osteoarthritis?',
      'Top differentials for weight loss + polyuria in a 6-yr-old cat?',
      'TPLO post-op rehab protocol for a Labrador?',
      'Maropitant dosing and contraindications in cats?',
      'Treatment options for feline hyperthyroidism?',
    ]

    function submitHeroSearch() {
      if (!heroQuery.value.trim()) return
      pendingQuery.value = heroQuery.value.trim()
      chatKey.value++
      heroSubmitted.value = true
    }

    function useHeroPrompt(prompt) {
      heroQuery.value = prompt
      nextTick(() => submitHeroSearch())
    }

    const primaryColor = computed(() => whitelabelBranding.getColor('primary') || '#667eea')
    const secondaryColor = computed(() => whitelabelBranding.getColor('secondary') || '#764ba2')
    const accentColor = computed(() => whitelabelBranding.getColor('accent') || '#667eea')

    const portalOrgSlug = computed(() => (
      whitelabelBranding.getCurrentOrganization()?.slug
      || whitelabelBranding.detectOrganizationSlug()
    ))

    const hubTitle = computed(() => {
      if (isVmaeDemoOrgSlug(portalOrgSlug.value)) {
        return `${whitelabelBranding.getAppName()} Learning Hub`
      }
      return 'Learning Hub'
    })

    const hubSubtitle = computed(() => {
      const branding = whitelabelBranding.getCurrentBranding()
      if (isVmaeDemoOrgSlug(portalOrgSlug.value) && branding?.tagline) {
        return `${branding.tagline} • Clinical guidance and continuing education`
      }
      return 'Clinical guidance powered by AI + PubMed • Discover continuing education'
    })

    const handleViewCourse = (course) => {
      if (course?.id) {
        router.push({ name: 'CourseDetail', params: { id: course.id } })
      }
    }

    return {
      activeView,
      complianceSubjectId,
      setActiveView,
      primaryColor,
      secondaryColor,
      accentColor,
      hubTitle,
      hubSubtitle,
      heroInput,
      heroQuery,
      heroFocused,
      heroSubmitted,
      heroDepth,
      pendingQuery,
      pendingSessionId,
      chatKey,
      suggestedPrompts,
      submitHeroSearch,
      useHeroPrompt,
      handleViewCourse,
    }
  }
}
</script>

<style scoped>
.learning-catalog-page {
  padding-bottom: 2.5rem;
  min-height: 100vh;
}

.learning-catalog-page.consult-active {
  max-width: 100%;
}

.header-actions {
  display: flex;
  gap: var(--spacing-md, 1rem);
  align-items: center;
  flex-shrink: 0;
}

.consult-entry {
  max-width: 720px;
}

.hero-depth-toggle {
  display: inline-flex;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
  padding: 0.25rem;
  background: #f3f4f6;
  border-radius: 12px;
}

.hero-depth-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: none;
  background: transparent;
  color: #6b7280;
  font-size: 0.875rem;
  font-weight: 600;
  padding: 0.55rem 1.1rem;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s;
}

.hero-depth-btn:hover:not(.active) {
  color: #374151;
  background: rgba(255, 255, 255, 0.7);
}

.hero-depth-btn.active {
  background: white;
  color: v-bind(primaryColor);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.hero-search-wrap {
  margin-bottom: 1.75rem;
}

.hero-search-box {
  display: flex;
  align-items: center;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 16px;
  padding: 0.5rem 0.5rem 0.5rem 1.25rem;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  transition: border-color 0.2s, box-shadow 0.2s;
  gap: 0.75rem;
}

.hero-search-box.focused {
  border-color: v-bind(primaryColor);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08), 0 0 0 3px color-mix(in srgb, v-bind(primaryColor) 15%, transparent);
}

.hero-search-icon {
  color: #9ca3af;
  font-size: 1.125rem;
  flex-shrink: 0;
}

.hero-search-box.focused .hero-search-icon {
  color: v-bind(primaryColor);
}

.hero-search-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 1.0625rem;
  color: #111827;
  background: transparent;
  min-width: 0;
  padding: 0.625rem 0;
}

.hero-search-input::placeholder {
  color: #9ca3af;
}

.hero-search-btn {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  background: v-bind(primaryColor);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, transform 0.1s, opacity 0.15s;
}

.hero-search-btn:hover:not(:disabled) {
  filter: brightness(1.1);
  transform: scale(1.04);
}

.hero-search-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.hero-suggestions {
  text-align: left;
  margin-top: 1.5rem;
}

.suggestions-label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.75rem;
}

.suggestions-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.suggestion-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1rem;
  background: white;
  border: 1.5px solid #e5e7eb;
  border-radius: 100px;
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
  white-space: nowrap;
}

.suggestion-chip:hover {
  border-color: v-bind(primaryColor);
  background: color-mix(in srgb, v-bind(primaryColor) 5%, white);
  color: v-bind(primaryColor);
}

.suggestion-chip i {
  font-size: 0.8125rem;
  color: v-bind(primaryColor);
}

.secondary-nav {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  padding-top: 1.5rem;
  margin-top: 1.5rem;
  border-top: 1px solid #e5e7eb;
}

.secondary-nav-link {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.9375rem;
  font-weight: 500;
  color: #6b7280;
  text-decoration: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: color 0.15s, background 0.15s;
}

.secondary-nav-link:hover {
  color: v-bind(primaryColor);
  background: color-mix(in srgb, v-bind(primaryColor) 6%, transparent);
}

.secondary-nav-link i {
  font-size: 0.875rem;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm, 0.5rem);
  padding: var(--spacing-sm, 0.75rem) var(--spacing-lg, 1.5rem);
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.2s ease;
  border: none;
  cursor: pointer;
  white-space: nowrap;
}

.btn-secondary {
  background: white;
  color: v-bind(primaryColor);
  border: 1px solid #e5e7eb;
}

.btn-secondary:hover {
  background: #f9fafb;
  border-color: #d1d5db;
}

.view-content {
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 768px) {
  .list-header {
    flex-direction: column;
  }

  .header-actions {
    width: 100%;
  }

  .btn {
    width: 100%;
    justify-content: center;
  }
}
</style>

